// Escalate ceremony.
//
// Escalation requires a non-empty reason at the moment of
// escalation. The analyst's reason is captured here and persisted on the
// new EscalationRecord row by CriteriaChecklistService.EscalateAsync.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";

interface EscalateModalProps {
  open: boolean;
  onClose: () => void;
  itemCode: string;
  escalationPath: string | null;
  onConfirm: (reason: string) => void | Promise<void>;
  pending?: boolean;
}

export function EscalateModal({
  open,
  onClose,
  itemCode,
  escalationPath,
  onConfirm,
  pending = false,
}: EscalateModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError("Please describe why you're escalating this item.");
      return;
    }
    setError(null);
    await onConfirm(reason);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Escalate ${itemCode}`}
      description="Routes this item to the catalog-defined escalation lane."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleConfirm()} disabled={pending}>
            {pending ? "Escalating…" : "Escalate"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-onyx m-0">
          <span className="font-display text-label uppercase tracking-label text-gold-shadow">
            Escalation path
          </span>
          <br />
          {escalationPath ?? "E2 — Underwriting Executive Lead"}
        </p>

        <div>
          <label className="font-display text-label uppercase tracking-label text-gold-shadow">
            Why are you escalating?
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
            placeholder="Describe the situation and what decision you're asking for."
            className="w-full mt-space-1 border border-border-light rounded-md p-space-2"
          />
          {error && (
            <div role="alert" className="text-status-error text-small mt-space-1">
              {error}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
